import { NotFound } from "@acme/ui/not-found";
import {
  createFileRoute,
  ErrorComponent,
  notFound,
} from "@tanstack/react-router";
import { ProjectView } from "~/components/projects/project-view";
import { PREVIEW_HEADERS } from "~/lib/cache-headers";
import { SECRET_URL } from "~/lib/secret-url";
import { seo } from "~/lib/seo";
import { $getProjectPreview } from "~/lib/server/project";
import * as m from "~/paraglide/messages.js";
import { getLocale } from "~/paraglide/runtime.js";

/**
 * Reviewer links: /projects/preview/<token> and /lv/projects/preview/<token>.
 *
 * Same rules as the article preview: never cached, never indexed, and no
 * Referer leaves the page, because the URL itself is the credential
 * (`PREVIEW_HEADERS`, on the 404 for a bad or expired token too).
 * `SECRET_URL` keeps every third-party script off the page (see __root.tsx).
 */
export const Route = createFileRoute("/(public)/projects/preview/$token")({
  loader: async ({ params: { token } }) => {
    try {
      const project = await $getProjectPreview({
        data: { locale: getLocale(), token },
      });
      return { project };
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === "Preview not found" ||
          error.message === "Project not found")
      ) {
        throw notFound();
      }
      throw error;
    }
  },
  headers: () => PREVIEW_HEADERS,
  head: ({ loaderData }) => {
    const page = seo({
      noindex: true,
      path: "/projects",
      title: `${loaderData?.project?.title ?? m.project_preview_title()} | ${m.project_preview_title()}`,
    });
    return {
      links: [],
      meta: [
        ...page.meta.filter((tag) => !("name" in tag && tag.name === "robots")),
        { content: "noindex, nofollow", name: "robots" },
        { content: "no-referrer", name: "referrer" },
      ],
      scripts: [],
    };
  },
  staticData: SECRET_URL,
  component: PreviewPage,
  errorComponent: ({ error }) => <ErrorComponent error={error} />,
  notFoundComponent: () => <NotFound>{m.project_preview_not_found()}</NotFound>,
});

function PreviewPage() {
  const { project } = Route.useLoaderData();

  return (
    <ProjectView
      banner={
        <p className="preview-banner" role="status">
          <strong>
            {project.isDraft
              ? m.project_preview_draft()
              : m.project_preview_live()}
          </strong>{" "}
          {m.blog_preview_note()}
        </p>
      }
      project={project}
    />
  );
}
